import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

interface SidebarContextValue {
  leftOpen: boolean;
  rightOpen: boolean;
  setLeftOpen: (open: boolean) => void;
  setRightOpen: (open: boolean) => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

interface SidebarProviderProps {
  children: React.ReactNode;
  // Backward compatible (maps to left)
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // New dual-side controls
  leftOpen?: boolean;
  rightOpen?: boolean;
  onLeftOpenChange?: (open: boolean) => void;
  onRightOpenChange?: (open: boolean) => void;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({
  children,
  open: legacyOpen,
  onOpenChange: onLegacyOpenChange,
  leftOpen,
  rightOpen,
  onLeftOpenChange,
  onRightOpenChange,
}) => {
  // Uncontrolled defaults: left open (desktop inline), right closed (overlay)
  const [leftUncontrolled, setLeftUncontrolled] = useState(() => {
    // Default closed on mobile, open on desktop
    const isDesktop =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(min-width: 768px)").matches;
    return isDesktop;
  });
  const [rightUncontrolled, setRightUncontrolled] = useState(false);

  const isLeftControlled =
    typeof leftOpen === "boolean" || typeof legacyOpen === "boolean";
  const isRightControlled = typeof rightOpen === "boolean";

  const resolvedLeftOpen =
    (typeof leftOpen === "boolean" ? leftOpen : legacyOpen) ?? leftUncontrolled;
  const resolvedRightOpen =
    typeof rightOpen === "boolean" ? rightOpen : rightUncontrolled;

  const setLeftOpen = (next: boolean) => {
    // On mobile, opening left should close right overlay
    const isDesktop =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(min-width: 768px)").matches;
    if (!isDesktop && next) {
      if (isRightControlled) {
        onRightOpenChange?.(false);
      } else {
        setRightUncontrolled(false);
      }
    }

    if (isLeftControlled) {
      // Prefer new callback, fallback to legacy
      onLeftOpenChange?.(next);
      onLegacyOpenChange?.(next);
    } else {
      setLeftUncontrolled(next);
    }
  };

  const setRightOpen = (next: boolean) => {
    // On mobile, opening right should close left sidebar
    const isDesktop =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(min-width: 768px)").matches;
    if (!isDesktop && next) {
      if (isLeftControlled) {
        onLeftOpenChange?.(false);
        onLegacyOpenChange?.(false);
      } else {
        setLeftUncontrolled(false);
      }
    }

    if (isRightControlled) {
      onRightOpenChange?.(next);
    } else {
      setRightUncontrolled(next);
    }
  };

  const value = useMemo(
    () => ({
      leftOpen: resolvedLeftOpen,
      rightOpen: resolvedRightOpen,
      setLeftOpen,
      setRightOpen,
    }),
    [resolvedLeftOpen, resolvedRightOpen]
  );

  useEffect(() => {
    const handleToggleShortcut = (event: KeyboardEvent) => {
      if (event.repeat) return;
      const activeElement = document.activeElement as HTMLElement | null;
      if (activeElement) {
        const tagName = activeElement.tagName;
        const isTypingElement =
          activeElement.isContentEditable ||
          tagName === "INPUT" ||
          tagName === "TEXTAREA" ||
          tagName === "SELECT";
        if (isTypingElement) {
          return;
        }
      }

      const pressedKey =
        typeof event.key === "string" ? event.key.toLowerCase() : "";

      if (pressedKey !== "b") {
        return;
      }

      const isMac =
        typeof navigator !== "undefined" &&
        /mac/i.test(navigator.platform || navigator.userAgent);

      const shouldToggle = isMac
        ? event.metaKey && !event.ctrlKey
        : event.ctrlKey && !event.metaKey;

      if (!shouldToggle) {
        return;
      }

      event.preventDefault();
      setLeftOpen(!resolvedLeftOpen);
    };

    window.addEventListener("keydown", handleToggleShortcut);
    return () => {
      window.removeEventListener("keydown", handleToggleShortcut);
    };
  }, [resolvedLeftOpen, setLeftOpen]);

  // Ensure initial state reflects current breakpoint if uncontrolled
  useEffect(() => {
    const isDesktop =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(min-width: 768px)").matches;
    if (!isLeftControlled) {
      setLeftUncontrolled(isDesktop);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure mutual exclusivity on mobile even when controlled from outside
  useEffect(() => {
    const isDesktop =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(min-width: 768px)").matches;
    if (!isDesktop && resolvedLeftOpen && resolvedRightOpen) {
      // Prefer closing the right overlay when both are open
      if (isRightControlled) {
        onRightOpenChange?.(false);
      } else {
        setRightUncontrolled(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedLeftOpen]);

  useEffect(() => {
    const isDesktop =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(min-width: 768px)").matches;
    if (!isDesktop && resolvedLeftOpen && resolvedRightOpen) {
      // If right opens afterwards, close left
      if (isLeftControlled) {
        onLeftOpenChange?.(false);
        onLegacyOpenChange?.(false);
      } else {
        setLeftUncontrolled(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedRightOpen]);

  // Lock body scroll on mobile when any overlay sidebar is open
  useEffect(() => {
    const isDesktop =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(min-width: 768px)").matches;

    const shouldLock = !isDesktop && (resolvedLeftOpen || resolvedRightOpen);

    if (typeof document !== "undefined") {
      const html = document.documentElement;
      const body = document.body;

      if (shouldLock) {
        html.style.overflow = "hidden";
        body.style.overflow = "hidden";
      } else {
        html.style.overflow = "";
        body.style.overflow = "";
      }
    }

    return () => {
      if (typeof document !== "undefined") {
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "";
      }
    };
  }, [resolvedLeftOpen, resolvedRightOpen]);

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error("useSidebar must be used within a SidebarProvider");
  return ctx;
};

export const useLeftSidebar = () => {
  const { leftOpen, setLeftOpen } = useSidebar();
  return { open: leftOpen, setOpen: setLeftOpen };
};

export const useRightSidebar = () => {
  const { rightOpen, setRightOpen } = useSidebar();
  return { open: rightOpen, setOpen: setRightOpen };
};

interface SidebarProps {
  children: React.ReactNode;
}

// Left sidebar (inline on desktop, drawer on mobile)
export const LeftSidebar: React.FC<SidebarProps> = ({ children }) => {
  const { open, setOpen } = useLeftSidebar();

  return (
    <>
      {/* Mobile underlay */}
      <div
        className={
          "md:hidden fixed left-0 right-0 bottom-0 top-14 z-[940] transition-opacity duration-200 " +
          (open
            ? "opacity-100 pointer-events-auto bg-black/20 backdrop-blur-sm"
            : "opacity-0 pointer-events-none")
        }
        onClick={() => setOpen(false)}
      />

      {/* Mobile drawer */}
      <div
        className={
          "md:hidden z-[960] bg-bg-primary dark:bg-gray-900 fixed top-14 left-0 bottom-0 w-[88vw] max-w-80 border-r border-border-secondary dark:border-gray-700 shadow-2xl shadow-black/25 rounded-r-2xl " +
          (open ? "translate-x-0" : "-translate-x-full") +
          " transition-transform duration-300 will-change-transform"
        }
      >
        {children}
      </div>

      {/* Desktop static column */}
      <aside
        className={
          "hidden md:block shrink-0  transition-[width] duration-300 h-screen " +
          (open ? "md:w-72" : "md:w-20")
        }
      >
        {children}
      </aside>
    </>
  );
};

// Right sidebar (always overlay on both desktop and mobile)
export const RightSidebar: React.FC<SidebarProps> = ({ children }) => {
  const { open, setOpen } = useRightSidebar();

  return (
    <>
      {/* Underlay */}
      <div
        className={
          "fixed left-0 right-0 bottom-0 top-16 z-[998] transition-opacity duration-200 " +
          (open
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none")
        }
        style={
          {
            // background: "hsl(var(--background-transparent))",
          }
        }
        onClick={() => setOpen(false)}
      />

      {/* Drawer */}
      <div
        className={
          "z-[999] bg-gradient-sidebar border-l shadow-2xl border-border/50 backdrop-blur-xl fixed top-16 right-0 bottom-0 w-80 md:w-[30vw] shadow-2xl shadow-black/30 " +
          (open ? "translate-x-0" : "translate-x-full") +
          " transition-transform duration-300"
        }
      >
        {children}
      </div>
    </>
  );
};

// Backward compatibility alias
export const Sidebar = LeftSidebar;

interface SidebarSectionProps {
  children: React.ReactNode;
  className?: string;
}

export const SidebarHeader: React.FC<SidebarSectionProps> = ({
  children,
  className,
}) => {
  return <div className={className}>{children}</div>;
};

export const SidebarFooter: React.FC<SidebarSectionProps> = ({
  children,
  className,
}) => {
  return <div className={className}>{children}</div>;
};

export const SidebarContent: React.FC<SidebarSectionProps> = ({
  children,
  className,
}) => {
  return (
    <div className={"h-full overflow-y-auto " + (className || "")}>
      {children}
    </div>
  );
};
